// src/components/HomeSolutions.jsx
import React, { useEffect, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { motion, AnimatePresence } from 'framer-motion';

const solutions = [
    {
        id: 0,
        title: 'Medication Management',
        description: `Finding the right medication can be life-changing. Our expert psychiatric team provides comprehensive medication management, carefully monitoring your progress and adjusting treatments to ensure optimal results with minimal side effects.`,
        link: '/med-management'
    },
    {
        id: 1,
        title: 'TMS Therapy',
        description: 'TMS is a breakthrough therapy for depression. It uses magnetic fields to stimulate brain regions affected by mental health disorders, offering a non-invasive alternative to medication.',
        link: '/tms'
    },
    {
        id: 2,
        title: 'Ketamine Treatments',
        description: 'Ketamine treatments provides rapid relief from depression, PTSD, and other mood disorders by promoting neural growth and resetting mood pathways.',
        link: '/ketamine'
    },
    {
        id: 3,
        title: 'ADHD Testing',
        description: 'Accurate ADHD testing helps us create customized treatment plans to help you or your loved one thrive at work, school, and home.',
        link: '/adhd-testing'
    },
];

const HomeSolutions = () => {
    const [activeId, setActiveId] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 950);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 950);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSolutionClick = (id) => {
        setActiveId(id);
    };

    return (
        <div id="home-solutions-container" className="container">
            <div id="home-solutions-content">
                <h2>Real Challenges. Real Solutions.</h2>
                <p className="large">
                    Mental health challenges are real—but so are solutions. Our innovative, physician-led treatments are designed to help you <strong>feel like yourself again.</strong>
                </p>
            </div>

            {isMobile ? (
                <div id="mobile-home-solutions">
                    {solutions.map((solution) => (
                        <div key={solution.id} className="accordion">
                            <a
                                href="#"
                                className={`accordion-header ${activeId === solution.id ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSolutionClick(solution.id);
                                }}
                            >
                                <div>
                                    <h3>{solution.title}</h3>
                                </div>
                                <div>
                                    <LazyLoadImage alt="Arrow" effect="blur" src="/img/icon-blue-arrow.svg" />
                                </div>
                            </a>

                            {/* Accordion Content */}
                            <AnimatePresence>
                                {activeId === solution.id && (
                                    <motion.div
                                        className="accordion-content"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <div>
                                            <h3>{solution.title}</h3>
                                            <p>{solution.description}</p>
                                            <a href={solution.link} className="button blue">
                                                Learn More
                                            </a>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            ) : (
                <div id="home-solutions">
                    <div>
                        {solutions.map((solution) => (
                            <a
                                href="#"
                                key={solution.id}
                                className={`home-solution ${activeId === solution.id ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSolutionClick(solution.id);
                                }}
                            >
                                <div><h3>{solution.title}</h3></div>
                                <div>
                                    <LazyLoadImage alt="Arrow" effect="blur" src="/img/icon-blue-arrow.svg" />
                                </div>
                            </a>
                        ))}
                    </div>
                    <div>
                        <AnimatePresence mode="wait">
                            {solutions.filter((s) => s.id === activeId).map((solution) => (
                                <motion.div
                                    key={solution.id}
                                    className="home-solution-tile active"
                                    initial={{ y: 100, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: 100, opacity: 0 }}
                                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                                >
                                    <h3>{solution.title}</h3>
                                    <p>{solution.description}</p>
                                    <a href={solution.link} className="button blue">
                                        Learn More
                                    </a>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomeSolutions;
